/**
 * Notification Action Handler Service
 *
 * Processes user interactions with notification action buttons.
 *
 * CRITICAL CONSTRAINTS:
 * - Use devLog() instead of console.log
 * - Zero any types - all explicit
 *
 * @see WI-037 Notification Action Handlers
 */

import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ActionPerformed } from '@capacitor/push-notifications';
import { CheckInService } from '@features/check-ins';
import { FirestoreService } from './firestore.service';
import { devLog, devError } from '@core';
import {
  NotificationActionType,
  ActionContext,
  ActionResult,
  ActionProcessor,
  QueuedAction,
  ActionErrorCode
} from '@core/types/notification-action.types';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class NotificationActionHandlerService {
  private readonly router = inject(Router);
  private readonly checkInService = inject(CheckInService);
  private readonly firestore = inject(FirestoreService);

  private processors = new Map<NotificationActionType, ActionProcessor>();
  private readonly _processing = signal<boolean>(false);
  private readonly _lastResult = signal<ActionResult | null>(null);

  /** Whether an action is currently being processed */
  readonly processing = this._processing.asReadonly();

  /** Result of the last processed action */
  readonly lastResult = this._lastResult.asReadonly();

  constructor() {
    this.registerProcessors();
  }

  /**
   * Handle notification action from Capacitor
   */
  async handleNotificationAction(
    action: ActionPerformed
  ): Promise<ActionResult> {
    devLog('[ActionHandler] Handling action:', action.actionId);

    const context = this.buildActionContext(action);

    // Queue if offline
    if (!navigator.onLine) {
      return this.queueAction(context);
    }

    return this.processAction(context);
  }

  /**
   * Process a notification action
   */
  async processAction(context: ActionContext): Promise<ActionResult> {
    this._processing.set(true);

    try {
      const processor = this.processors.get(context.actionType);

      if (!processor) {
        throw this.createError('INVALID_ACTION', `Unknown action: ${context.actionType}`);
      }

      // Validate action
      const isValid = await processor.validate(context);
      if (!isValid) {
        throw this.createError('INVALID_STATE', 'Action validation failed');
      }

      // Process action
      const result = await processor.process(context);
      this._lastResult.set(result);

      // Handle deep link
      if (result.success && result.result?.deepLink) {
        await this.navigateToDeepLink(result.result.deepLink);
      }

      devLog('[ActionHandler] Action completed:', result);
      return result;

    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Processing failed';
      devError('[ActionHandler] Action failed:', message);

      const result: ActionResult = {
        success: false,
        actionId: context.actionId,
        processedAt: new Date(),
        error: {
          code: this.getErrorCode(error),
          message,
          retryable: this.isRetryable(error)
        }
      };

      this._lastResult.set(result);
      return result;

    } finally {
      this._processing.set(false);
    }
  }

  /**
   * Queue action for offline processing
   */
  async queueAction(context: ActionContext): Promise<ActionResult> {
    const queued: QueuedAction = {
      id: this.generateId('queue'),
      action: context,
      createdAt: new Date(),
      attempts: 0,
      lastAttempt: null,
      status: 'pending',
      error: null
    };

    // Store in localStorage (IndexedDB fallback pattern from spec)
    const queue = this.getQueue();
    queue.push(queued);
    this.saveQueue(queue);

    devLog('[ActionHandler] Action queued:', queued.id);

    return {
      success: true,
      actionId: context.actionId,
      processedAt: new Date(),
      result: {
        message: 'Action queued for processing when online'
      }
    };
  }

  /**
   * Process all queued actions (call when coming online)
   */
  async processQueuedActions(): Promise<void> {
    const queue = this.getQueue();
    const pending = queue.filter(q => q.status === 'pending');

    devLog('[ActionHandler] Processing queue:', pending.length, 'actions');

    for (const item of pending) {
      item.status = 'processing';
      item.lastAttempt = new Date();
      item.attempts++;

      try {
        const result = await this.processAction(item.action);

        if (result.success) {
          item.status = 'completed';
        } else if (item.attempts >= 3) {
          item.status = 'failed';
          item.error = result.error?.message ?? 'Max retries exceeded';
        } else {
          item.status = 'pending';
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        item.error = message;
        if (item.attempts >= 3) {
          item.status = 'failed';
        } else {
          item.status = 'pending';
        }
      }
    }

    // Remove completed, keep pending/failed
    const updated = queue.filter(q => q.status !== 'completed');
    this.saveQueue(updated);
  }

  /**
   * Register action processors
   */
  private registerProcessors(): void {
    // Check-in processor
    this.processors.set(NotificationActionType.CHECK_IN, {
      canProcess: (action) => !!action.payload.checkInId,
      validate: async (action) => {
        const checkIn = await this.checkInService.getCheckIn(action.payload.checkInId);
        return !!checkIn && ['active', 'nudge', 'escalating'].includes(checkIn.status);
      },
      process: async (action) => {
        await this.checkInService.resolveCheckIn(
          action.payload.checkInId,
          'Checked in via notification'
        );

        return {
          success: true,
          actionId: action.actionId,
          processedAt: new Date(),
          result: {
            message: 'Check-in complete!',
            deepLink: `/check-ins/${action.payload.checkInId}`
          }
        };
      }
    });

    // Snooze processor
    this.processors.set(NotificationActionType.SNOOZE, {
      canProcess: (action) => !!action.payload.checkInId,
      validate: async (action) => {
        const checkIn = await this.checkInService.getCheckIn(action.payload.checkInId);
        return !!checkIn && ['active', 'nudge'].includes(checkIn.status);
      },
      process: async (action) => {
        const durationMinutes = 30;

        await this.checkInService.snoozeCheckIn(
          action.payload.checkInId,
          durationMinutes
        );

        const snoozeUntil = new Date(Date.now() + durationMinutes * 60 * 1000);

        return {
          success: true,
          actionId: action.actionId,
          processedAt: new Date(),
          result: {
            message: `Snoozed until ${snoozeUntil.toLocaleTimeString()}`
          }
        };
      }
    });

    // View details processor
    this.processors.set(NotificationActionType.VIEW_DETAILS, {
      canProcess: (action) => !!action.payload.checkInId,
      validate: async () => true,
      process: async (action) => {
        return {
          success: true,
          actionId: action.actionId,
          processedAt: new Date(),
          result: {
            message: 'Opening check-in details',
            deepLink: `/check-ins/${action.payload.checkInId}`
          }
        };
      }
    });
  }

  /**
   * Build action context from Capacitor action
   */
  private buildActionContext(action: ActionPerformed): ActionContext {
    const data = action.notification.data as Record<string, string>;

    return {
      actionId: this.generateId('action'),
      actionType: this.mapActionType(action.actionId),
      notificationId: data['notificationId'] ?? '',
      payload: {
        checkInId: data['checkInId'] ?? '',
        userId: data['userId'] ?? '',
        timestamp: new Date().toISOString(),
        source: this.getPlatform(),
        appState: this.getAppState()
      }
    };
  }

  /**
   * Map action ID string to enum
   */
  private mapActionType(actionId: string): NotificationActionType {
    const mapping: Record<string, NotificationActionType> = {
      'check-in': NotificationActionType.CHECK_IN,
      'snooze': NotificationActionType.SNOOZE,
      'view': NotificationActionType.VIEW_DETAILS
    };

    return mapping[actionId] ?? NotificationActionType.DISMISS;
  }

  /**
   * Navigate to deep link
   */
  private async navigateToDeepLink(link: string): Promise<void> {
    // Convert relative path to absolute if needed
    const absoluteLink = link.startsWith('/') ? link : `/${link}`;
    // Ensure we navigate to the check-in within the tabs structure
    const route = absoluteLink.startsWith('/tabs') ? absoluteLink : `/tabs${absoluteLink}`;
    await this.router.navigateByUrl(route);
  }

  /**
   * Get current platform
   */
  private getPlatform(): 'ios' | 'android' | 'web' {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') return 'ios';
    if (platform === 'android') return 'android';
    return 'web';
  }

  /**
   * Get current app state
   */
  private getAppState(): 'foreground' | 'background' | 'terminated' {
    return document.visibilityState === 'visible' ? 'foreground' : 'background';
  }

  /**
   * Get error code from error
   */
  private getErrorCode(error: unknown): ActionErrorCode {
    if (error instanceof Error && 'code' in error) {
      return (error as Error & { code: ActionErrorCode }).code;
    }
    return 'PROCESSING_ERROR';
  }

  /**
   * Check if error is retryable
   */
  private isRetryable(error: unknown): boolean {
    const code = this.getErrorCode(error);
    return ['NETWORK_ERROR', 'TIMEOUT'].includes(code);
  }

  /**
   * Create typed error
   */
  private createError(code: ActionErrorCode, message: string): Error & { code: ActionErrorCode } {
    const error = new Error(message) as Error & { code: ActionErrorCode };
    error.code = code;
    return error;
  }

  /**
   * Generate unique ID
   */
  private generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Get offline queue from storage
   */
  private getQueue(): QueuedAction[] {
    const stored = localStorage.getItem('notification_action_queue');
    if (!stored) return [];
    try {
      const parsed = JSON.parse(stored) as unknown[];
      return parsed.map((item: unknown) => {
        const q = item as Record<string, unknown>;
        return {
          id: q['id'] as string,
          action: q['action'] as ActionContext,
          createdAt: new Date(q['createdAt'] as string),
          attempts: q['attempts'] as number,
          lastAttempt: q['lastAttempt'] ? new Date(q['lastAttempt'] as string) : null,
          status: q['status'] as 'pending' | 'processing' | 'completed' | 'failed',
          error: q['error'] as string | null
        };
      });
    } catch {
      return [];
    }
  }

  /**
   * Save offline queue to storage
   */
  private saveQueue(queue: QueuedAction[]): void {
    localStorage.setItem('notification_action_queue', JSON.stringify(queue));
  }
}
